/**
 * Ajustes gerais — saudação, ausência, horário, inatividade e distribuição.
 *
 * ## A frase que esta tela existe para dizer na hora certa
 *
 * §7.3 da spec 05: *"toda saída automática é uma saída"*. O aviso de inatividade pode cair **fora da
 * janela de 24 horas** do WhatsApp e simplesmente não sair. A spec é explícita sobre onde avisar:
 * *"no momento de configurar a automação, o painel diz que aquela mensagem vai exigir template no canal
 * oficial. Descobrir no disparo é descobrir tarde."*
 *
 * Por isso os avisos por canal vêm do servidor e ficam **no topo**, antes dos controles.
 *
 * ## Tudo desligado por padrão
 *
 * Cada controle aqui manda mensagem ao cliente do cliente. Ligar por omissão faria o produto falar em nome
 * de quem não pediu.
 */

import { useEffect, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Escolha, EstadoDaFalha, falhaDe, useSessao, Gaveta, Ficha } from "@hugi/ui";
import { Carregando, Dica, Selo, type Tom } from "@hugi/ui/kit";
import {
  usePoliticasDeSla,
  useCatalogos,
  salvarPoliticaDeSla,
  arquivarPoliticaDeSla,
  salvarConfiguracoes,
  useConfiguracoes,
  type ConfiguracaoDoTenant,
} from "./dados.js";

const DIAS = ["Domingo", "Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado"] as const;

/** Os fusos do Brasil, mais Lisboa. Digitar fuso à mão é errar fuso à mão. */
const FUSOS = [
  "America/Sao_Paulo",
  "America/Manaus",
  "America/Cuiaba",
  "America/Belem",
  "America/Fortaleza",
  "America/Recife",
  "America/Rio_Branco",
  "America/Noronha",
  "Europe/Lisbon",
] as const;

/* ---------- peças comuns às abas de Ajustes (spec 22) ---------- */

const TOM_DO_ESTADO: Readonly<Record<"ok" | "degradado" | "ruim" | "neutro", Tom>> = {
  ok: "sucesso",
  degradado: "atencao",
  ruim: "perigo",
  neutro: "neutro",
};

/** A situação de um ajuste no `Selo` do kit (cor E texto). */
export function Situacao(props: { readonly estado: "ok" | "degradado" | "ruim" | "neutro"; readonly rotulo: string }) {
  return <Selo tom={TOM_DO_ESTADO[props.estado]}>{props.rotulo}</Selo>;
}

/**
 * Um botão da v2 que, desabilitado, diz POR QUÊ (no `title`, como o `Botao` de sempre) — nunca um
 * controle cinza calado.
 */
export function Acao(props: {
  readonly children: ReactNode;
  readonly onClick?: () => void;
  readonly tom?: "primaria" | "secundaria" | "perigo";
  readonly desabilitado?: boolean;
  readonly motivo?: string;
  readonly pequeno?: boolean;
}) {
  const tom = props.tom === "primaria" ? " is-primario" : props.tom === "perigo" ? " is-perigo" : "";
  return (
    <button
      type="button"
      className={`v2-botao${tom}${props.pequeno ? " is-pequeno" : ""}`}
      onClick={props.onClick}
      disabled={props.desabilitado}
      title={props.desabilitado ? props.motivo : undefined}
    >
      {props.children}
    </button>
  );
}

/**
 * A ABA de Ajustes: o corpo rola por dentro e o rodapé (o Salvar, o Criar) fica preso embaixo, fora
 * do que rola — o defeito histórico desta tela era o Salvar fora de alcance, no fim de oito cartões.
 */
export function AbaDeAjustes(props: { readonly barra?: ReactNode; readonly rodape?: ReactNode; readonly children: ReactNode }) {
  return (
    <div className="hugi-ajustes-aba">
      {props.barra ? <div className="hugi-ajustes-barra">{props.barra}</div> : null}
      <div className="hugi-ajustes-rolagem" tabIndex={0} role="region" aria-label="Conteúdo da aba">
        {props.children}
      </div>
      {props.rodape ? <footer className="hugi-ajustes-rodape">{props.rodape}</footer> : null}
    </div>
  );
}

export function Gerais() {
  const sessao = useSessao();
  const cliente = useQueryClient();
  const q = useConfiguracoes();
  const podeMexer = sessao.permissoes.includes("manage_catalog");

  const [f, setF] = useState<ConfiguracaoDoTenant | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  // O formulário é preenchido quando a leitura chega, e não a cada render: sobrescrever o que a pessoa
  // está digitando a cada revalidação é o defeito clássico de formulário controlado por query.
  useEffect(() => {
    if (q.data && !f) setF(q.data.configuracao);
  }, [q.data, f]);

  if (q.error) {
    return (
      <EstadoDaFalha
        falha={falhaDe(q.error) ?? { tipo: "inesperado", erro: { erro: "?" }, status: 0 }}
        aoRepetir={() => void q.refetch()}
      />
    );
  }
  if (q.isPending || !f) return <Carregando>Carregando os ajustes…</Carregando>;

  const mudar = <C extends keyof ConfiguracaoDoTenant>(campo: C, valor: ConfiguracaoDoTenant[C]) => {
    setF((antes) => (antes ? { ...antes, [campo]: valor } : antes));
    setSalvo(false);
  };

  const mudarDia = (indice: number, valor: string) => {
    const atual = f.horario ?? Array<string | null>(7).fill(null);
    const novo = atual.map((v, i) => (i === indice ? (valor.trim() === "" ? null : valor) : v));
    // Todos os dias vazios = sem horário definido. É diferente de "sete dias fechados", e o servidor trata
    // os dois de forma diferente: sem horário, a ausência é recusada em vez de responder sempre.
    mudar("horario", novo.every((v) => v === null) ? null : novo);
  };

  const salvar = async () => {
    setSalvando(true);
    setErro(null);
    const r = await salvarConfiguracoes(cliente, f);
    setSalvando(false);
    if (r.ok) {
      setSalvo(true);
      return;
    }
    // A frase vem do servidor: ela é que sabe por que a combinação não serve.
    setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar.");
  };

  return (
    <AbaDeAjustes
      rodape={
        <>
          {erro ? (
            <p role="alert" className="v2-erro">
              {erro}
            </p>
          ) : null}
          {salvo ? (
            <span role="status" className="hugi-ajustes-ok">
              Salvo.
            </span>
          ) : null}
          {f.atualizadaEm ? (
            <span className="hugi-ajustes-mudo">
              Última alteração {new Date(f.atualizadaEm).toLocaleString("pt-BR")}
              {f.atualizadaPor ? ` por ${f.atualizadaPor}` : ""}
            </span>
          ) : null}
          {podeMexer ? (
            <Acao tom="primaria" onClick={() => void salvar()} desabilitado={salvando} motivo="Salvando.">
              {salvando ? "Salvando…" : "Salvar"}
            </Acao>
          ) : (
            <span className="hugi-ajustes-mudo">
              Só leitura — mexer exige <code>manage_catalog</code>.
            </span>
          )}
        </>
      }
    >
      {/*
        Os avisos por canal ANTES dos controles. Depois deles seria tarde: a pessoa já teria ligado a
        automação achando que ela sempre sai.
      */}
      {q.data!.avisos.length > 0 ? (
        <div className="hugi-ajustes-bloco">
          <Gaveta titulo={`Como as mensagens automáticas se comportam em cada canal (${q.data!.avisos.length})`}>
            {q.data!.avisos.map((a) => (
              <p key={a.canal} role="note" className="mb-1.5">
                <strong className="font-medium">{a.canal}: </strong>
                {a.aviso}
              </p>
            ))}
          </Gaveta>
        </div>
      ) : null}

      {!podeMexer ? (
        <p className="hugi-ajustes-bloco hugi-ajustes-mudo">
          Você pode ver estas configurações e não alterá-las — mexer exige <code>manage_catalog</code>.{" "}
          <Dica texto="Elas estão aqui porque explicam por que uma mensagem automática saiu." />
        </p>
      ) : null}

      {/*
        Colunas (masonry), e não grade: os cartões têm alturas diferentes — o Horário tem oito linhas, a
        Saudação uma — e uma grade alinha as linhas deixando vazios do tamanho da diferença. Visto em
        produção em 03/09. `break-inside-avoid` impede um cartão de partir entre as colunas.
      */}
      <div className="hugi-ajustes-colunas">
      <Bloco
        titulo="Saudação"
        resumo={[{ rotulo: "Estado", valor: <Situacao estado={f.saudacaoLigada ? "ok" : "neutro"} rotulo={f.saudacaoLigada ? "ligada" : "desligada"} /> }]}
        explicacao="Enviada na primeira mensagem de uma conversa nova. Ela responde a algo que acabou de chegar, então sai em qualquer canal."
      >
        <Interruptor
          rotulo="Enviar saudação automática"
          ligado={f.saudacaoLigada}
          desabilitado={!podeMexer}
          aoMudar={(v) => mudar("saudacaoLigada", v)}
        />
        {f.saudacaoLigada ? (
          <Campo
            rotulo="Texto da saudação"
            valor={f.saudacaoTexto ?? ""}
            desabilitado={!podeMexer}
            exemplo="Olá! Recebemos sua mensagem e já vamos atender."
            aoMudar={(v) => mudar("saudacaoTexto", v)}
          />
        ) : null}
      </Bloco>

      <Bloco
        titulo="Horário de atendimento"
        resumo={resumoDoHorario(f.horario, f.fuso)}
        explicacao="Deixe o dia vazio para fechado. O horário é lido no fuso escolhido abaixo — não no fuso do servidor."
      >
        <div className="hugi-ajustes-dias">
          {DIAS.map((dia, i) => (
            <label key={dia} className="hugi-ajustes-dia">
              <span>{dia}</span>
              <input
                value={f.horario?.[i] ?? ""}
                disabled={!podeMexer}
                onChange={(e) => mudarDia(i, e.target.value)}
                placeholder="08:00-18:00"
                className="hugi-ajustes-mono"
              />
            </label>
          ))}
        </div>

        <label className="hugi-ajustes-campo">
          <span>Fuso horário</span>
          <select value={f.fuso} disabled={!podeMexer} onChange={(e) => mudar("fuso", e.target.value)}>
            {FUSOS.map((z) => (
              <option key={z} value={z}>
                {z}
              </option>
            ))}
          </select>
        </label>
      </Bloco>

      <Bloco
        titulo="Fora do horário"
        resumo={[{ rotulo: "Estado", valor: <Situacao estado={f.ausenciaLigada ? "ok" : "neutro"} rotulo={f.ausenciaLigada ? "ligada" : "desligada"} /> }]}
        explicacao="Depende do horário acima: sem horário definido não existe 'fora do horário', e a mensagem responderia sempre."
      >
        <Interruptor
          rotulo="Responder fora do horário"
          ligado={f.ausenciaLigada}
          desabilitado={!podeMexer || !f.horario}
          motivo={!f.horario ? "Defina o horário de atendimento primeiro." : undefined}
          aoMudar={(v) => mudar("ausenciaLigada", v)}
        />
        {f.ausenciaLigada ? (
          <Campo
            rotulo="Mensagem de ausência"
            valor={f.ausenciaTexto ?? ""}
            desabilitado={!podeMexer}
            exemplo="Estamos fora do horário de atendimento. Respondemos assim que abrirmos."
            aoMudar={(v) => mudar("ausenciaTexto", v)}
          />
        ) : null}
      </Bloco>

      {/*
        A RESPOSTA A QUEM LIGA (0115, o resto do H77). A explicação diz o que ela NÃO sabe: o aviso da
        ligação chega quando ela toca, antes de alguém atender no celular — então ela serve a quem não
        atende ligação pelo WhatsApp, e dizer isso aqui evita que alguém a ligue esperando "só quando
        ninguém atendeu".
      */}
      <Bloco
        titulo="Ligações"
        resumo={[{ rotulo: "Responder", valor: <Situacao estado={f.ligacaoRespostaLigada ? "ok" : "neutro"} rotulo={f.ligacaoRespostaLigada ? "ligada" : "desligada"} /> }]}
        explicacao="Só no número conectado por QR. A mensagem sai quando a ligação chega — antes de se saber se alguém vai atender no celular —, uma vez por conversa por dia. Serve para quem não atende ligação pelo WhatsApp."
      >
        <Interruptor
          rotulo="Responder a quem liga"
          ligado={f.ligacaoRespostaLigada}
          desabilitado={!podeMexer}
          aoMudar={(v) => mudar("ligacaoRespostaLigada", v)}
        />
        {f.ligacaoRespostaLigada ? (
          <Campo
            rotulo="Mensagem para quem liga"
            valor={f.ligacaoRespostaTexto ?? ""}
            desabilitado={!podeMexer}
            exemplo="Não atendemos ligações por aqui. Mande a sua mensagem que respondemos já."
            aoMudar={(v) => mudar("ligacaoRespostaTexto", v)}
          />
        ) : null}
      </Bloco>

      <PoliticasDeSla podeMexer={podeMexer} />

      <Bloco
        titulo="Pesquisa de satisfação"
        resumo={[{ rotulo: "Pedir avaliação", valor: <Situacao estado={f.avaliacaoLigada ? "ok" : "neutro"} rotulo={f.avaliacaoLigada ? "ligada" : "desligada"} /> }]}
        explicacao="Sai quando o atendimento é encerrado. A nota que o contato responder aparece no relatório — em distribuição e taxa de resposta, nunca em média."
      >
        <Interruptor
          rotulo="Pedir avaliação ao encerrar"
          ligado={f.avaliacaoLigada}
          desabilitado={!podeMexer}
          aoMudar={(v) => mudar("avaliacaoLigada", v)}
        />
        {f.avaliacaoLigada ? (
          <>
            <Campo
              rotulo="O pedido"
              valor={f.avaliacaoTexto ?? ""}
              desabilitado={!podeMexer}
              exemplo="Como você avalia nosso atendimento? Responda de 1 a 5, sendo 5 ótimo."
              aoMudar={(v) => mudar("avaliacaoTexto", v)}
            />
            {/*
              A escala tem de estar no texto, e o servidor recusa salvar sem ela. O aviso aparece aqui para
              a pessoa entender a recusa antes de tomá-la: sem "de 1 a 5", o contato responde "ótimo" ou
              "10", e nenhuma das duas vira nota.
            */}
            {!/[1-5]/.test(f.avaliacaoTexto ?? "") && (
              <p className="hugi-ajustes-atencao">
                Escreva a escala no texto — algo como “responda de 1 a 5”. Sem isso, a resposta do contato
                não vira nota.
              </p>
            )}
            <p className="hugi-ajustes-mudo">
              O pedido passa pelas regras do canal.{" "}
              <Dica texto="Como toda mensagem automática, o pedido passa pelas regras do canal: encerrar um atendimento cuja última mensagem do contato foi há mais de 24 horas significa que a pesquisa não sai — e isso vale igual para a sua equipe." />
            </p>
          </>
        ) : null}
      </Bloco>

      <Bloco
        titulo="Encerramento por inatividade"
        resumo={[
          {
            rotulo: "Encerra depois de",
            valor: f.inatividadeMinutos === null ? <Situacao estado="neutro" rotulo="desligado" /> : `${f.inatividadeMinutos} min`,
          },
        ]}
        explicacao="Fecha o atendimento quando ninguém escreve há um tempo. O motivo fica marcado como automático nos relatórios — encerrar por silêncio não é alguém decidir que resolveu."
      >
        <label className="hugi-ajustes-linha">
          <span>Encerrar depois de</span>
          <input
            type="number"
            min={5}
            max={10080}
            value={f.inatividadeMinutos ?? ""}
            disabled={!podeMexer}
            onChange={(e) =>
              mudar("inatividadeMinutos", e.target.value === "" ? null : Number(e.target.value))
            }
            placeholder="desligado"
            className="hugi-ajustes-numero"
          />
          <span className="hugi-ajustes-mudo">minutos sem mensagem (de 5 a 10.080)</span>
        </label>

        {f.inatividadeMinutos !== null ? (
          <>
            <Interruptor
              rotulo="Avisar antes de encerrar"
              ligado={f.inatividadeAvisa}
              desabilitado={!podeMexer}
              aoMudar={(v) => mudar("inatividadeAvisa", v)}
            />
            {f.inatividadeAvisa ? (
              <Campo
                rotulo="Aviso"
                valor={f.inatividadeTexto ?? ""}
                desabilitado={!podeMexer}
                exemplo="Vamos encerrar este atendimento por inatividade. Se ainda precisar, escreva."
                aoMudar={(v) => mudar("inatividadeTexto", v)}
              />
            ) : null}
          </>
        ) : null}
      </Bloco>

      <Bloco
        titulo="Distribuição e privacidade"
        explicacao="Como a conversa chega a quem atende, e o que quem atende vê."
        resumo={[
          {
            rotulo: "Assinar as respostas",
            valor: (
              <Situacao
                estado={(f.assinatura ?? "nunca") === "nunca" ? "neutro" : "ok"}
                rotulo={
                  f.assinatura === "sempre"
                    ? "sempre"
                    : f.assinatura === "atendente_escolhe"
                      ? "quem atende decide"
                      : "nunca"
                }
              />
            ),
          },
          { rotulo: "Ocultar o telefone", valor: <Situacao estado={f.ocultarTelefone ? "ok" : "neutro"} rotulo={f.ocultarTelefone ? "ligada" : "desligada"} /> },
          { rotulo: "Carteirização", valor: <Situacao estado={f.carteirizacao ? "ok" : "neutro"} rotulo={f.carteirizacao ? "ligada" : "desligada"} /> },
          { rotulo: "Quem começa chat da equipe", valor: f.chatQuemInicia === "supervisores" ? "supervisor e administrador" : "todos" },
          { rotulo: "Avisos no celular", valor: f.numeroParaAlertas ? f.numeroParaAlertas : "—" },
        ]}
      >
        <Interruptor
          rotulo="Devolver ao último atendente (carteirização)"
          explicacao="O contato que volta cai com quem já falou com ele, se essa pessoa estiver na fila. Em equipe pequena isso concentra trabalho — por isso é opcional."
          ligado={f.carteirizacao}
          desabilitado={!podeMexer}
          aoMudar={(v) => mudar("carteirizacao", v)}
        />
        <label className="hugi-ajustes-campo">
          <span>Quem pode começar um chat da equipe</span>
          <select
            value={f.chatQuemInicia}
            disabled={!podeMexer}
            onChange={(e) => mudar("chatQuemInicia", e.target.value === "supervisores" ? "supervisores" : "todos")}
          >
            <option value="todos">Todos da equipe</option>
            <option value="supervisores">Só supervisor e administrador</option>
          </select>
        </label>
        <p className="hugi-ajustes-mudo">
          Vale para a primeira mensagem entre duas pessoas.{" "}
          <Dica texto="Responder a quem já falou com você é sempre livre." />
        </p>
        <Interruptor
          rotulo="Ocultar o telefone do contato"
          explicacao="O atendente vê o nome e não o número. Reduz o que uma equipe grande consegue levar embora."
          ligado={f.ocultarTelefone}
          desabilitado={!podeMexer}
          aoMudar={(v) => mudar("ocultarTelefone", v)}
        />
        {/*
          TRÊS MODOS, e não um interruptor (H75, 0059).

          O do meio é o que o mercado assume, e a razão não é preferência: numa clínica, "seu exame
          ficou pronto" assinado por Ana é bom; "você está com uma pendência financeira" assinado por
          Ana põe o nome dela numa conversa que ela não quer levar para a rua. **Quem sabe qual é
          qual é quem está escrevendo.**
        */}
        <div className="hugi-ajustes-grupo">
          <div className="hugi-ajustes-rotulo">Assinar as respostas com o nome de quem atendeu</div>
          <div className="flex flex-col gap-1.5">
            {(
              [
                ["nunca", "Nunca", "A resposta sai sem nome. O cliente fala com a empresa."],
                ["atendente_escolhe", "Quem atende decide", "Aparece um botão no compositor, e a escolha é de cada mensagem."],
                ["sempre", "Sempre", "Toda resposta leva o nome de quem escreveu."],
              ] as const
            ).map(([valor, titulo, descricao]) => (
              <Escolha
                key={valor}
                nome="assinatura"
                valor={valor}
                marcada={(f.assinatura ?? "nunca") === valor}
                aoEscolher={() => { if (podeMexer) mudar("assinatura", valor); }}
                titulo={titulo}
                descricao={descricao}
              />
            ))}
          </div>
        </div>
        {/*
          O telefone pessoal de quem é dono do negócio.

          Fica aqui, e não escondido no provisionamento, por uma razão do plano Profissional: quem o
          contrata NÃO tem equipe e não abre o painel no dia a dia — ele descobre que tem cliente
          esperando quando pega o celular. O número para onde esse aviso vai é escolha dele, e a
          única tela em que ele passa é esta.

          Vazio é resposta legítima, e a explicação diz isso: quem tem painel não precisa de aviso.
        */}
        <Campo
          rotulo="Telefone para avisos (opcional)"
          valor={f.numeroParaAlertas ?? ""}
          desabilitado={!podeMexer}
          exemplo="27999998888"
          aoMudar={(v) => mudar("numeroParaAlertas", v)}
        />
        <p className="hugi-ajustes-mudo">
          Seu WhatsApp pessoal.{" "}
          <Dica texto="Quando o assistente precisar te chamar, o aviso chega aí — com quem está esperando e por quê. Deixe vazio se você acompanha pelo painel." />
        </p>
      </Bloco>

      </div>
    </AbaDeAjustes>
  );
}

/**
 * O cartão-fato de um ajuste.
 *
 * Fechado, ele mostra o ESTADO (uma `Ficha` de duas a quatro linhas, com chip) e o botão "Ajustar";
 * aberto, mostra os controles. É a regra 5 de `docs/mercado/bench-de-telas.md`: o cartão mostra o
 * fato, o formulário fica atrás de um clique. Até 03/09 esta tela abria com oito formulários ao
 * mesmo tempo, e o owner chamou aquilo de "um monte de caixa espalhada".
 *
 * A explicação longa continua existindo — dentro do cartão aberto, onde quem vai mexer a lê.
 */
/** "Seg a Sex 08:00-18:00 · Sáb 08:00-12:00 · Dom fechado", ou "não definido". */
function resumoDoHorario(horario: readonly (string | null)[] | null, fuso: string) {
  if (!horario || horario.every((h) => !h)) {
    return [
      { rotulo: "Horário", valor: <Situacao estado="degradado" rotulo="não definido" /> },
      { rotulo: "Fuso", valor: fuso },
    ];
  }
  const semana = horario.slice(1, 6);
  const iguais = semana.every((h) => h === semana[0]);
  const linhas = iguais
    ? [{ rotulo: "Seg a Sex", valor: semana[0] ?? "fechado" }]
    : DIAS.slice(1, 6).map((d, i) => ({ rotulo: d, valor: semana[i] ?? "fechado" }));
  return [
    ...linhas,
    { rotulo: "Sábado", valor: horario[6] ?? "fechado" },
    { rotulo: "Domingo", valor: horario[0] ?? "fechado" },
    { rotulo: "Fuso", valor: fuso },
  ];
}

function Bloco(props: {
  readonly titulo: string;
  readonly explicacao: string;
  /** Sem resumo, o cartão nasce aberto e sem o botão — é o caso de quem ainda é lista, como o SLA. */
  readonly resumo?: readonly { readonly rotulo: ReactNode; readonly valor: ReactNode }[];
  readonly children: ReactNode;
}) {
  const [aberto, setAberto] = useState(!props.resumo);
  return (
    <section className="hugi-ajustes-bloco">
      <div className="hugi-ajustes-bloco-topo">
        <h2>{props.titulo}</h2>
        {/* A explicação mora no (i), fora do título (o nome do cartão continua só o título). */}
        <Dica texto={props.explicacao} />
        {props.resumo ? (
          <Acao pequeno onClick={() => setAberto((a) => !a)}>
            {aberto ? "Fechar" : "Ajustar"}
          </Acao>
        ) : null}
      </div>
      {aberto ? (
        props.children
      ) : (
        <Ficha itens={props.resumo ?? []} />
      )}
    </section>
  );
}

function Interruptor(props: {
  readonly rotulo: string;
  readonly explicacao?: string;
  readonly ligado: boolean;
  readonly desabilitado?: boolean;
  /** Por que está desabilitado. Sem isto, um controle cinza é um enigma. */
  readonly motivo?: string | undefined;
  readonly aoMudar: (v: boolean) => void;
}) {
  return (
    <div className="hugi-ajustes-interruptor">
      <label {...(props.motivo ? { title: props.motivo } : {})}>
        <input
          type="checkbox"
          checked={props.ligado}
          disabled={props.desabilitado}
          onChange={(e) => props.aoMudar(e.target.checked)}
        />
        <span>
          {props.rotulo}
          {props.motivo ? <span className="hugi-ajustes-atencao">{props.motivo}</span> : null}
        </span>
      </label>
      {props.explicacao ? <Dica texto={props.explicacao} /> : null}
    </div>
  );
}

function Campo(props: {
  readonly rotulo: string;
  readonly valor: string;
  readonly exemplo: string;
  readonly desabilitado?: boolean;
  readonly aoMudar: (v: string) => void;
}) {
  return (
    <label className="hugi-ajustes-campo">
      <span>{props.rotulo}</span>
      <textarea
        value={props.valor}
        disabled={props.desabilitado}
        rows={2}
        onChange={(e) => props.aoMudar(e.target.value)}
        placeholder={props.exemplo}
      />
      {/* O contador aparece perto do limite, e não sempre: 4096 caracteres é muito, e mostrar "12/4096"
          numa saudação de doze letras é ruído. */}
      {props.valor.length > 3500 ? (
        <span className="hugi-ajustes-atencao">
          {props.valor.length} de 4096 caracteres
        </span>
      ) : null}
    </label>
  );
}

/**
 * As políticas de SLA (§3 da spec 09).
 *
 * ## Por que aqui, e não numa tela própria
 *
 * Política de SLA é da mesma natureza de horário de atendimento e de encerramento por inatividade: é a
 * declaração de como esta operação funciona. Uma tela própria a esconderia de quem está justamente
 * configurando a operação.
 *
 * ## A lista vem da mais específica para a mais geral
 *
 * É a ordem da precedência (fila > canal > tenant), e ver a lista nessa ordem é o que faz a pessoa entender
 * qual ganha **sem ler documentação**. A alternativa — ordem alfabética com uma nota explicando a regra — é
 * como uma configuração de precedência fica incompreensível.
 */
function PoliticasDeSla(props: { readonly podeMexer: boolean }) {
  const cliente = useQueryClient();
  const q = usePoliticasDeSla();
  const catalogos = useCatalogos();

  const [abrindo, setAbrindo] = useState(false);
  const [nome, setNome] = useState("");
  const [primeira, setPrimeira] = useState("30");
  const [resolucao, setResolucao] = useState("");
  const [filaId, setFilaId] = useState("");
  const [emHorario, setEmHorario] = useState(false);
  const [agenteConta, setAgenteConta] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const salvar = async () => {
    setErro(null);
    const r = await salvarPoliticaDeSla(cliente, {
      nome,
      metaPrimeiraRespostaMin: primeira === "" ? null : Number(primeira),
      metaResolucaoMin: resolucao === "" ? null : Number(resolucao),
      filaId: filaId || null,
      contaApenasEmHorario: emHorario,
      agenteContaComoResposta: agenteConta,
    });
    if (!r.ok) {
      setErro(r.falha?.erro.mensagem ?? "Não foi possível salvar.");
      return;
    }
    setNome("");
    setResolucao("");
    setFilaId("");
    setAbrindo(false);
  };

  const itens = q.data?.itens ?? [];

  return (
    <Bloco
      titulo="Compromisso de resposta (SLA)"
      explicacao="Mede se a sua equipe respondeu dentro do combinado. Sem política, o Hugi mede quanto demorou — mas não tem contra o que comparar."
    >
      {itens.length === 0 ? (
        <p className="hugi-ajustes-mudo">
          Nenhuma política. Nenhuma conversa aparece como fora do prazo, e isso está certo: não há prazo.
        </p>
      ) : (
        <ul className="hugi-ajustes-lista">
          {itens.map((p) => (
            <li key={p.id} className="hugi-ajustes-item">
              <div className="min-w-0 flex-1">
                <p className="font-medium">
                  {p.nome}
                  <span className="ml-2 text-xs font-normal text-[color:var(--tinta-fraca)]">
                    {p.filaNome
                      ? `fila ${p.filaNome}`
                      : p.canal
                        ? `canal ${p.canal}`
                        : "todo o atendimento"}
                  </span>
                </p>
                <p className="text-xs text-[color:var(--tinta-fraca)]">
                  {p.metaPrimeiraRespostaMin
                    ? `primeira resposta em ${p.metaPrimeiraRespostaMin} min`
                    : "não mede primeira resposta"}
                  {" · "}
                  {p.metaResolucaoMin ? `resolução em ${p.metaResolucaoMin} min` : "não mede resolução"}
                  {p.contaApenasEmHorario ? " · só conta no horário de atendimento" : ""}
                  {p.agenteContaComoResposta ? " · a resposta do assistente conta" : ""}
                </p>
              </div>
              <button
                type="button"
                className="v2-link"
                disabled={!props.podeMexer}
                title={!props.podeMexer ? "Arquivar política exige a permissão manage_catalog." : undefined}
                onClick={() => void arquivarPoliticaDeSla(cliente, p.id)}
              >
                arquivar
              </button>
            </li>
          ))}
        </ul>
      )}

      {props.podeMexer && (
        <button type="button" className="v2-botao is-pequeno hugi-ajustes-inicio" onClick={() => setAbrindo(!abrindo)}>
          {abrindo ? "Fechar" : "Nova política"}
        </button>
      )}

      {abrindo && (
        <div className="hugi-ajustes-subform">
          <input
            aria-label="Nome da política"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            placeholder="Ex.: Recepção responde rápido"
          />
          <div className="hugi-ajustes-linha">
            <span>Primeira resposta em</span>
            <input
              type="number"
              aria-label="Primeira resposta em minutos"
              className="hugi-ajustes-numero"
              min={1}
              value={primeira}
              onChange={(e) => setPrimeira(e.target.value)}
            />
            <span>min · resolução em</span>
            <input
              type="number"
              aria-label="Resolução em minutos"
              className="hugi-ajustes-numero"
              min={1}
              value={resolucao}
              onChange={(e) => setResolucao(e.target.value)}
              placeholder="—"
            />
            <span>min</span>
            <Dica texto="Deixe em branco o que você não quer medir. No relatório, o que não é medido aparece como um traço — e não como 0%." />
          </div>
          <label className="hugi-ajustes-campo">
            <span>
              Vale para{" "}
              <Dica texto="A política de uma fila ganha da geral. É assim que “o financeiro responde em 4h e a recepção em 10 minutos” funciona." />
            </span>
            <select value={filaId} onChange={(e) => setFilaId(e.target.value)}>
              <option value="">todo o atendimento</option>
              {(catalogos.data?.filas ?? []).map((f) => (
                <option key={f.id} value={f.id}>
                  a fila {f.nome}
                </option>
              ))}
            </select>
          </label>
          <Interruptor
            rotulo="Contar só dentro do horário de atendimento."
            explicacao="Sem isto, uma conversa que chega às 22h já nasce atrasada. Com isto, o relatório mostra os dois números — porque só o útil esconde que o cliente esperou a noite inteira."
            ligado={emHorario}
            aoMudar={setEmHorario}
          />
          <Interruptor
            rotulo="A resposta do assistente conta como primeira resposta."
            explicacao="Desligado de propósito: se o assistente responde em 2 segundos, o indicador fica verde para sempre e deixa de dizer quando gente respondeu."
            ligado={agenteConta}
            aoMudar={setAgenteConta}
          />
          {erro && <p role="alert" className="v2-erro">{erro}</p>}
          <div>
            <Acao tom="primaria" onClick={() => void salvar()} desabilitado={nome.trim() === ""} motivo="Dê um nome à política.">
              Criar política
            </Acao>
          </div>
        </div>
      )}
    </Bloco>
  );
}
